import type { RevenueScope, RevenueValues, SegmentEvolution, VatBasis } from '@app/model/bi/Bi';
import { formatDate, MONTH_LABELS, revenueOf, roundCents } from '../Bi.utilities';

// The pure functions of the Segment evolution card (no React, no chart engine).

// 'revenue' and 'adr' follow the revenue switches of the page (ROO2-236).
export type Metric = 'roomNights' | 'revenue' | 'adr' | 'mix';

// At most 4 named series and one gray "All other" (chart rule: 7 series at most).
export const NAMED = 4;

export type Line = {
	id: string, label: string, roomNights: number[], occupiedRooms: number[], other: boolean,
	roomRevenue: (number | null)[], roomRevenueNet: (number | null)[], totalRevenue: number[], totalRevenueNet: (number | null)[],
};

// A sum with a part that has no value has no value (ROO2-236 R2): never a partial sum.
export const sumOrNull = (values: (number | null)[]): number | null =>
	values.some(v => v === null) ? null : values.reduce<number>((sum, v) => sum + (v ?? 0), 0);

// ROO2-280 decision 23: each revenue of a line is rounded to the cent before the display, as in the Excel file; ADR and
// Mix % follow from the rounded values.
const cents = (values: (number | null)[]): (number | null)[] => values.map(roundCents);
const centsOf = (values: number[]): number[] => values.map(v => roundCents(v) ?? 0);

// The four revenues of a line in one period.
export const valuesAt = (l: Line, i: number): RevenueValues => ({
	roomRevenue: l.roomRevenue[i], roomRevenueNet: l.roomRevenueNet[i], totalRevenue: l.totalRevenue[i], totalRevenueNet: l.totalRevenueNet[i],
});

// The top 4 groups (or segments) by total revenue with VAT of the period keep their name; all the others add up to "All
// other". The rank does not follow the revenue switches, so a group keeps its colour when the user switches (D4).
// "Not grouped" and "No segment" never take a named slot.
export const linesOf = (data: SegmentEvolution): Line[] => {
	const revenue = (s: { totalRevenue: number[] }) => s.totalRevenue.reduce((a, b) => a + b, 0);
	const ranked = data.series.filter(s => s.kind === 'GROUP' || s.kind === 'SEGMENT').filter(s => revenue(s) > 0)
		.sort((a, b) => revenue(b) - revenue(a));
	const named = ranked.slice(0, NAMED);
	// ROO2-315 part c1 (Q1): a series with occupied rooms or revenue only (a day use, a posting) also adds to "All other".
	const rest = data.series.filter(s => !named.includes(s)
		&& (s.roomNights.some(v => v > 0) || s.occupiedRooms.some(v => v > 0) || s.totalRevenue.some(v => v !== 0)));
	const lines: Line[] = named.map(s => ({
		id: s.key, label: s.title, roomNights: s.roomNights, occupiedRooms: s.occupiedRooms, other: false,
		roomRevenue: cents(s.roomRevenue), roomRevenueNet: cents(s.roomRevenueNet), totalRevenue: centsOf(s.totalRevenue), totalRevenueNet: cents(s.totalRevenueNet),
	}));
	if (rest.length > 0) {
		lines.push({
			id: 'all-other',
			label: rest.length === 1 ? rest[0].title : 'All other',
			roomNights: data.periods.map((_, i) => rest.reduce((sum, s) => sum + s.roomNights[i], 0)),
			occupiedRooms: data.periods.map((_, i) => rest.reduce((sum, s) => sum + s.occupiedRooms[i], 0)),
			roomRevenue: cents(data.periods.map((_, i) => sumOrNull(rest.map(s => s.roomRevenue[i])))),
			roomRevenueNet: cents(data.periods.map((_, i) => sumOrNull(rest.map(s => s.roomRevenueNet[i])))),
			totalRevenue: centsOf(data.periods.map((_, i) => rest.reduce((sum, s) => sum + s.totalRevenue[i], 0))),
			totalRevenueNet: cents(data.periods.map((_, i) => sumOrNull(rest.map(s => s.totalRevenueNet[i])))),
			other: true,
		});
	}
	return lines;
};

// The sold room nights of all lines in each period: the base of the Mix %.
export const periodNights = (data: SegmentEvolution, lines: Line[]): number[] =>
	data.periods.map((_, i) => lines.reduce((sum, l) => sum + l.roomNights[i], 0));

// The periods with no stacked revenue bar (R4): one line of the period has no value of the selected revenue, and a
// shorter stack would be a partial sum.
export const revenueGaps = (data: SegmentEvolution, lines: Line[], scope: RevenueScope, vat: VatBasis): boolean[] =>
	data.periods.map((_, i) => lines.some(l => revenueOf(valuesAt(l, i), scope, vat) === null));

// The revenues of every line in every period with imported stays, for the note of the caption.
export const figuresOf = (data: SegmentEvolution, lines: Line[]): RevenueValues[] =>
	data.periods.flatMap((p, i) => p.hasData ? lines.map(l => valuesAt(l, i)) : []);

// The value of a line in each period for the metric: no value for a period with no imported stay; the revenue of the
// switches, with no value in a gap of `gaps`; ADR = that revenue / occupied rooms (ROO2-315 part c1: the room nights
// without report rules; no value with no occupied room or no revenue,
// only at that point: it is a line); Mix = the share of the line in the room nights of the period (`totals`).
export const metricValues = (data: SegmentEvolution, l: Line, metric: Metric, totals: number[], scope: RevenueScope, vat: VatBasis,
	gaps: boolean[]): (number | null)[] =>
	data.periods.map((p, i) => {
		if (!p.hasData) return null;
		if (metric === 'roomNights') return l.roomNights[i];
		const money = revenueOf(valuesAt(l, i), scope, vat);
		if (metric === 'revenue') return gaps[i] ? null : money;
		if (metric === 'adr') return l.occupiedRooms[i] === 0 || money === null ? null : money / l.occupiedRooms[i];
		return totals[i] === 0 ? null : l.roomNights[i] / totals[i];
	});

// The table view of the card for a metric and the revenue switches (ROO2-280 D6): one column per line, one row per period
// with imported stays ("Feb 2026", or the first day of a week). The "Table" sheet of the Excel file holds the same cells:
// the R4 test compares the two.
export type EvolutionTable = {
	lines: (Line & { values: (number | null)[] })[],
	rows: { key: string, label: string, from: string, to: string, values: (number | null)[] }[],
};
export const tableOf = (data: SegmentEvolution, metric: Metric, scope: RevenueScope, vat: VatBasis): EvolutionTable => {
	const lines = linesOf(data);
	const totals = periodNights(data, lines);
	const gaps = revenueGaps(data, lines, scope, vat);
	const withValues = lines.map(l => ({ ...l, values: metricValues(data, l, metric, totals, scope, vat, gaps) }));
	return {
		lines: withValues,
		rows: data.periods.flatMap((p, i) => p.hasData ? [{
			key: p.key, from: p.from, to: p.to,
			label: data.grain === 'MONTH' ? `${MONTH_LABELS[Number(p.from.slice(5, 7)) - 1]} ${p.from.slice(0, 4)}` : formatDate(p.from),
			values: withValues.map(l => l.values[i]),
		}] : []),
	};
};
